import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { mediaQuery } from '../../tokens/breakpoints';
import { rem } from '../../tokens/spacing';

export type PaginationSize = 'sm' | 'md' | 'lg';

export interface PaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  totalCount: number;
  currentPage: number;
  pageSize: number;
  totalPages: number;
  visibleCount?: number;
  showPageSize?: boolean;
  pageSizeOptions?: number[];
  onPageChange: (page: number) => void;
  onPageSizeChange?: (size: number) => void;
  semanticId?: string;
  /** Controls page-button sizing. `md` (default) matches legacy table pagination. */
  size?: PaginationSize;
}

const DOTS = '...';

function usePaginationRange(
  totalPages: number,
  current: number,
  siblingCount: number,
): Array<number | typeof DOTS> {
  if (totalPages <= 0) return [];
  const totalNumbers = siblingCount * 2 + 5;
  if (totalPages <= totalNumbers) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const leftSibling = Math.max(current - siblingCount, 1);
  const rightSibling = Math.min(current + siblingCount, totalPages);
  const showLeftEllipsis = leftSibling > 2;
  const showRightEllipsis = rightSibling < totalPages - 1;

  if (!showLeftEllipsis && showRightEllipsis) {
    const left = Array.from({ length: 3 + 2 * siblingCount }, (_, i) => i + 1);
    return [...left, DOTS, totalPages];
  }
  if (showLeftEllipsis && !showRightEllipsis) {
    const right = Array.from(
      { length: 3 + 2 * siblingCount },
      (_, i) => totalPages - (3 + 2 * siblingCount) + 1 + i,
    );
    return [1, DOTS, ...right];
  }
  if (showLeftEllipsis && showRightEllipsis) {
    const middle = Array.from(
      { length: rightSibling - leftSibling + 1 },
      (_, i) => leftSibling + i,
    );
    return [1, DOTS, ...middle, DOTS, totalPages];
  }
  return Array.from({ length: totalPages }, (_, i) => i + 1);
}

const PaginationRoot = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: ${rem(12)};
  padding: ${rem(16)} ${rem(12)} ${rem(14)};
  font-family: var(--ui-font-sans);

  ${mediaQuery.sm} {
    padding: ${rem(20)} ${rem(20)} ${rem(18)};
    gap: ${rem(16)};
  }

  ${mediaQuery.lg} {
    padding: ${rem(26)} ${rem(32)} ${rem(24)} ${rem(32)};
    flex-direction: row;
  }
`;

const TotalItems = styled.span`
  color: var(--ui-pagination-muted, var(--ui-text-subtle));
  font-size: ${rem(14)};
  line-height: ${rem(35)};
`;

const PageList = styled.ul`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: ${rem(2)};
  padding-left: 0;
  list-style: none;
  margin: 0;
`;

const PAGE_SIZE_MAP = {
  sm: { padding: `${rem(4)} ${rem(8)}`, fontSize: rem(12), lineHeight: rem(16) },
  md: { padding: `${rem(5)} ${rem(11)}`, fontSize: rem(16), lineHeight: rem(22) },
  lg: { padding: `${rem(8)} ${rem(14)}`, fontSize: rem(18), lineHeight: rem(24) },
} as const;

const PageItem = styled.li.withConfig({
  shouldForwardProp: (prop) => !['$active', '$size'].includes(prop),
})<{ $active?: boolean; $size: PaginationSize }>`
  padding: ${({ $size }) => PAGE_SIZE_MAP[$size].padding};
  border-radius: ${rem(6)};
  cursor: pointer;

  span {
    color: var(--ui-pagination-text, var(--ui-text-muted));
    font-size: ${({ $size }) => PAGE_SIZE_MAP[$size].fontSize};
    line-height: ${({ $size }) => PAGE_SIZE_MAP[$size].lineHeight};
  }

  ${({ $active }) =>
    $active
      ? `
    background-color: var(--ui-pagination-active-bg, var(--ui-interactive-light));
    span {
      color: var(--ui-pagination-active-color, var(--ui-interactive));
      font-weight: 600;
    }
  `
      : `
    &:hover {
      background-color: var(--ui-pagination-active-bg, var(--ui-interactive-light));
      span {
        color: var(--ui-pagination-active-color, var(--ui-interactive));
        font-weight: 600;
      }
    }
  `}
`;

const RowItems = styled.div`
  display: flex;
  gap: ${rem(4)};
  align-items: center;
`;

const PerPageLabel = styled.span`
  color: var(--ui-pagination-muted, var(--ui-text-subtle));
  font-size: ${rem(14)};
  line-height: ${rem(35)};
`;

const CustomSelect = styled.div`
  position: relative;
  cursor: pointer;
  background-color: var(--ui-background-muted, #f3f3f3);
  border-radius: 6rem;
  display: flex;
  align-items: center;
  height: fit-content;
  z-index: 0;

  select {
    font-size: ${rem(14)};
    line-height: ${rem(18)};
    font-weight: 500;
    -webkit-appearance: none;
    cursor: pointer;
    background-color: transparent;
    z-index: 1;
    position: relative;
    padding: ${rem(7)} ${rem(20)} ${rem(7)} ${rem(10)};
    border: none;
    font-family: var(--ui-font-sans);
    color: var(--ui-text);

    &:focus,
    &:hover {
      outline: none;
      box-shadow: none;
    }
  }

  .material-symbols-rounded {
    position: absolute;
    right: 0px;
    top: 50%;
    transform: translateY(-50%);
    color: var(--ui-text-muted);
  }
`;

export const Pagination = React.forwardRef<HTMLDivElement, PaginationProps>(function Pagination(
  {
    totalCount,
    currentPage,
    pageSize,
    totalPages,
    visibleCount,
    showPageSize = false,
    pageSizeOptions = [10, 20, 50, 100, 200],
    onPageChange,
    onPageSizeChange,
    semanticId,
    size = 'md',
    ...rest
  },
  ref,
) {
  const semanticProps = getSemanticAttributes(semanticId);
  const paginationRange = usePaginationRange(totalPages, currentPage, 1);
  const displayCount = visibleCount ?? 0;

  return (
    <PaginationRoot ref={ref} {...semanticProps} {...rest}>
      <TotalItems>
        {displayCount} of {totalCount} items
      </TotalItems>

      {totalPages > 1 && (
        <PageList>
          {paginationRange.map((pageNumber, index) => {
            if (pageNumber === DOTS) {
              return (
                <PageItem key={`dots-${index}`} $size={size}>
                  <span>&#8230;</span>
                </PageItem>
              );
            }
            return (
              <PageItem
                key={pageNumber}
                $active={Number(pageNumber) === currentPage}
                $size={size}
                onClick={() => onPageChange(Number(pageNumber))}
              >
                <span>{pageNumber}</span>
              </PageItem>
            );
          })}
        </PageList>
      )}

      {showPageSize && (
        <RowItems>
          <PerPageLabel>Per page</PerPageLabel>
          <CustomSelect>
            <select value={pageSize} onChange={(e) => onPageSizeChange?.(Number(e.target.value))}>
              {pageSizeOptions.map((size) => (
                <option key={size} value={size}>
                  {size} rows
                </option>
              ))}
            </select>
            <span className="material-symbols-rounded">arrow_drop_down</span>
          </CustomSelect>
        </RowItems>
      )}
    </PaginationRoot>
  );
});

Pagination.displayName = 'Pagination';
